import React, { useState } from 'react';
import { Table, Dropdown, Button, Tooltip, Modal, Input, message } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import AudienceExpandedRow from './AudienceExpandedRow';
import { useExcludeClient, useRestoreClient } from '../../../../../hooks/query/useEvents';
import { getRequest, postRequest } from '../../../../../core/apiRequests';

/**
 * Render a sortable column header
 */
const renderSortableHeader = (label, field, sortConfig, onSort) => {
  const isActive = sortConfig?.field === field;
  const isAscend = isActive && sortConfig?.order === 'ascend';
  const isDescend = isActive && sortConfig?.order === 'descend';

  return (
    <div
      role="button"
      tabIndex={0}
      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
      onClick={() => onSort && onSort(field)}
      onKeyDown={e => e.key === 'Enter' && onSort && onSort(field)}
    >
      <span>{label}</span>
      <span style={{ display: 'flex', flexDirection: 'column', fontSize: 10 }}>
        <FontAwesomeIcon
          icon={['fas', 'caret-up']}
          style={{ color: isAscend ? '#1890ff' : '#bfbfbf', marginBottom: -3 }}
        />
        <FontAwesomeIcon
          icon={['fas', 'caret-down']}
          style={{ color: isDescend ? '#1890ff' : '#bfbfbf', marginTop: -3 }}
        />
      </span>
    </div>
  );
};

/**
 * AudienceTable component for displaying event audience with expandable rows
 */
const AudienceTable = ({
  audience,
  loading,
  eventId,
  event,
  auth,
  pagination,
  sortConfig,
  onSort,
  onMessage,
  onScheduleAppointment,
}) => {
  const history = useHistory();
  const excludeClientMutation = useExcludeClient();
  const restoreClientMutation = useRestoreClient();
  const [notesModal, setNotesModal] = useState(null); // { activity, record }
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [activityRecord, setActivityRecord] = useState(null); // record for activity dropdown

  const { data: activityList = [] } = useQuery(
    ['activity-list', auth?.merchantId],
    () => getRequest({ params: ['client', 'activityList', auth.merchantId], loadingStatusOff: true }),
    { enabled: !!auth?.merchantId, refetchOnWindowFocus: false },
  );

  const saveActivity = async (activity, record, activityNotes = null) => {
    const clientId = record.clientId || record.id;
    try {
      setSaving(true);
      await postRequest({
        params: ['clients', 'saveClientActivity'],
        data: {
          clientId,
          associateId: auth?.userId,
          storeId: record.storeId || auth?.storeId,
          notes: activityNotes,
          activityID: activity.id,
        },
        loadingStatusOff: true,
      });
      message.success('Activity saved');
    } catch {
      message.error('Failed to save activity');
    } finally {
      setSaving(false);
    }
  };

  const handleActivityClick = (activity, record) => {
    if (activity.inc_notes === 1) {
      setNotesModal({ activity, record });
      setNotes('');
    } else {
      saveActivity(activity, record);
    }
  };

  const handleNotesSubmit = async () => {
    if (!notesModal) return;
    await saveActivity(notesModal.activity, notesModal.record, notes || null);
    setNotesModal(null);
    setNotes('');
  };

  const handleClientClick = clientId => {
    history.push(`/Clients/client-details/${clientId}`);
  };

  const handleMessage = record => {
    if (onMessage) {
      onMessage(record);
    }
  };

  const handleRemoveFromAudience = async record => {
    const clientId = record.clientId || record.id;
    try {
      await excludeClientMutation.mutateAsync({ eventId, clientId });
    } catch (error) {
      console.error('Failed to remove from audience:', error);
    }
  };

  const handleRestoreToAudience = async record => {
    const clientId = record.clientId || record.id;
    try {
      await restoreClientMutation.mutateAsync({ eventId, clientId });
    } catch (error) {
      console.error('Failed to restore to audience:', error);
    }
  };

  const getActionMenuItems = record => {
    const isExcluded = !!record.exclusion;

    return [
      {
        key: 'message',
        icon: <FontAwesomeIcon icon={['fal', 'comment']} />,
        label: 'Send Message',
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation();
          handleMessage(record);
        },
      },
      {
        key: 'activity',
        icon: <FontAwesomeIcon icon={['fal', 'chart-line']} />,
        label: 'Add Client Activity',
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation();
          setActivityRecord(record);
        },
      },
      {
        key: 'appointment',
        icon: <FontAwesomeIcon icon={['fal', record.appointments?.[0] ? 'calendar-pen' : 'calendar-plus']} />,
        label: record.appointments?.[0] ? 'Edit Appointment' : 'Schedule Appointment',
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation();
          if (onScheduleAppointment) {
            onScheduleAppointment(record);
          }
        },
      },
      /* eslint-disable indent */
      isExcluded
        ? {
            key: 'restore',
            icon: <FontAwesomeIcon icon={['fal', 'undo']} />,
            label: 'Restore to Audience',
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              handleRestoreToAudience(record);
            },
          }
        : {
            key: 'remove',
            icon: <FontAwesomeIcon icon={['fal', 'ban']} />,
            label: 'Exclude from Audience',
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              handleRemoveFromAudience(record);
            },
          },
      /* eslint-enable indent */
    ];
  };

  const formatDate = date => {
    if (!date) return '-';
    return dayjs(date).format('MM/DD/YYYY');
  };

  const formatDateTime = date => {
    if (!date) return 'No Appointment';
    return dayjs(date).format('MM/DD/YYYY [at] h:mma');
  };

  const formatCurrency = amount => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const columns = [
    {
      title: renderSortableHeader('Name', 'name', sortConfig, onSort),
      dataIndex: 'name',
      key: 'name',
      width: 180,
      render: (text, record) => {
        const clientId = record.clientId || record.id;
        const name = text || `${record.firstName || ''} ${record.lastName || ''}`.trim() || 'Unknown';
        const isExcluded = !!record.exclusion;
        return (
          <div className="client-name-cell">
            <span
              className="client-name-link"
              onClick={e => {
                e.stopPropagation();
                handleClientClick(clientId);
              }}
            >
              {name}
            </span>
            {isExcluded && (
              <Tooltip title="Excluded from audience">
                <FontAwesomeIcon icon={['fal', 'ban']} className="excluded-icon" />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: renderSortableHeader('Associate', 'associate', sortConfig, onSort),
      dataIndex: 'associate',
      key: 'associate',
      width: 150,
      render: associate => {
        // Handle associate as object or string
        if (associate && typeof associate === 'object') {
          return associate.name || `${associate.firstName || ''} ${associate.lastName || ''}`.trim() || 'Unassigned';
        }
        return associate || 'Unassigned';
      },
    },
    {
      title: renderSortableHeader('Last Contacted', 'lastContactDate', sortConfig, onSort),
      dataIndex: 'lastContactDate',
      key: 'lastContacted',
      width: 120,
      render: text => formatDate(text),
    },
    {
      title: renderSortableHeader('Appointment', 'appointmentDate', sortConfig, onSort),
      dataIndex: 'appointmentDate',
      key: 'appointment',
      width: 180,
      render: (text, record) => formatDateTime(text || record.appointments?.[0]?.appointmentDate),
    },
    {
      title: renderSortableHeader('Attributed Sales', 'attributedSales', sortConfig, onSort),
      dataIndex: 'attributedSales',
      key: 'sales',
      width: 130,
      render: text => formatCurrency(text),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => {
        const recordId = record.clientId || record.id;
        const isActivityOpen = (activityRecord?.clientId || activityRecord?.id) === recordId;
        const activityItems = activityList.map(act => ({
          key: act.id,
          label: act.description?.length > 20 ? `${act.description.slice(0, 20)}...` : act.description,
          onClick: ({ domEvent }) => {
            domEvent.stopPropagation();
            setActivityRecord(null);
            handleActivityClick(act, record);
          },
        }));
        const menuProps = isActivityOpen
          ? { items: activityItems, style: { maxHeight: 300, overflowY: 'auto' } }
          : { items: getActionMenuItems(record) };
        return (
          <Dropdown
            menu={menuProps}
            trigger={['click']}
            placement="bottomRight"
            open={isActivityOpen || undefined}
            onOpenChange={open => { if (!open && isActivityOpen) setActivityRecord(null); }}
          >
            <Button size="small" onClick={e => e.stopPropagation()}>
              Actions
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  // Handle table changes (pagination only - sorting is handled by custom headers)
  const handleTableChange = paginationInfo => {
    if (!pagination.onChange) return;
    const pageChanged = paginationInfo.current !== pagination.current;
    const sizeChanged = paginationInfo.pageSize !== pagination.pageSize;
    if (pageChanged || sizeChanged) {
      pagination.onChange(paginationInfo.current, paginationInfo.pageSize);
    }
  };

  return (
    <>
      <Table
        className="audience-table"
        columns={columns}
        dataSource={audience}
        loading={loading}
        rowKey={record => record.clientId || record.id}
        pagination={pagination}
        onChange={handleTableChange}
        expandable={{
          expandedRowRender: record => (
            <AudienceExpandedRow clientId={record.clientId || record.id} eventCreatedAt={event?.createdAt} />
          ),
          expandRowByClick: true,
          expandIcon: ({ expanded, onExpand, record }) => (
            <FontAwesomeIcon
              icon={['fal', expanded ? 'chevron-down' : 'chevron-right']}
              onClick={e => {
                e.stopPropagation();
                onExpand(record, e);
              }}
              style={{ cursor: 'pointer', marginRight: 8 }}
            />
          ),
        }}
        scroll={{ x: 900 }}
      />
      <Modal
        title="Add Activity Notes"
        open={!!notesModal}
        onCancel={() => setNotesModal(null)}
        onOk={handleNotesSubmit}
        okText="Save"
        confirmLoading={saving}
        destroyOnClose
      >
        <p style={{ marginBottom: 8 }}>{notesModal?.activity?.description}</p>
        <Input.TextArea
          rows={3}
          placeholder="Add notes..."
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </Modal>
    </>
  );
};

AudienceTable.propTypes = {
  audience: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    createdAt: PropTypes.string,
  }),
  auth: PropTypes.shape({
    merchantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    storeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  pagination: PropTypes.shape({
    current: PropTypes.number,
    pageSize: PropTypes.number,
    total: PropTypes.number,
    onChange: PropTypes.func,
  }),
  sortConfig: PropTypes.shape({
    field: PropTypes.string,
    order: PropTypes.oneOf(['ascend', 'descend', null]),
  }),
  onSort: PropTypes.func,
  onMessage: PropTypes.func,
  onScheduleAppointment: PropTypes.func,
};

AudienceTable.defaultProps = {
  audience: [],
  loading: false,
  event: null,
  auth: null,
  pagination: {},
  sortConfig: { field: null, order: null },
  onSort: null,
  onMessage: null,
  onScheduleAppointment: null,
};

export default AudienceTable;
